import { commitMembershipChange, createDmGroup, deserializeGroup, encryptApplicationMessage, exportGroupInfo, exportGroupSecret, generateKeyPackages, generateSignatureKeyPair, groupEpoch, groupMembers, joinExternal, joinFromWelcome, makeClientConfig, processIncoming, serializeGroup, } from './mls.js';
import { openEnvelope, sealEnvelope, } from './envelope.js';
import { bytesEqual, fromBase64, toBase64 } from './encoding.js';
/** A commit from the server log could not be applied: this device's group state is unusable. */
class BrokenGroupError extends Error {
}
export class ConflictError extends Error {
    details;
    constructor(details) {
        super('conflict');
        this.details = details;
    }
}
const KEY_PACKAGE_TARGET = 50;
const KEY_PACKAGE_REFILL_BELOW = 20;
/** How long to stop trying to add a device that had no key packages left. */
const NO_KEY_PACKAGES_RETRY_MS = 10 * 60_000;
export class DmCryptoEngine {
    api;
    store;
    userId;
    deviceName;
    identity = null;
    groups = new Map();
    locks = new Map();
    deviceKeys = new Map();
    /** Devices that had no key packages to claim, and when to try adding them again. */
    noKeyPackagesUntil = new Map();
    /** When this device last asked others for keys, per channel. */
    keysRequestedAt = new Map();
    listeners = new Set();
    config = makeClientConfig((m) => this.resolveKey(m));
    status = new Map();
    constructor(api, store, userId, deviceName) {
        this.api = api;
        this.store = store;
        this.userId = userId;
        this.deviceName = deviceName;
    }
    get deviceId() {
        return this.identity?.deviceId ?? null;
    }
    get identityKey() {
        return this.identity ? toBase64(this.identity.signaturePublicKey) : null;
    }
    on(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    emit(e) {
        if (e.type === 'status')
            this.status.set(e.channelId, e.status);
        for (const l of this.listeners)
            l(e);
    }
    /* ------------------------------------------------------------- lifecycle */
    /** Load or create this device's identity, register it, and top up key packages. */
    async init() {
        let stored = await this.store.get('identity');
        if (stored && stored.userId !== this.userId) {
            await this.wipe();
            stored = undefined;
        }
        if (stored) {
            const device = await this.api.registerDevice(this.deviceName, stored.publicKey);
            if (device.id !== stored.deviceId) {
                // The server no longer knows our device (session ended): start over.
                await this.wipe();
                stored = undefined;
            }
        }
        if (!stored) {
            const kp = await generateSignatureKeyPair();
            const publicKey = toBase64(kp.publicKey);
            const device = await this.api.registerDevice(this.deviceName, publicKey);
            stored = {
                userId: this.userId,
                deviceId: device.id,
                publicKey,
                privateKey: toBase64(kp.privateKey),
            };
            await this.store.set('identity', stored);
        }
        this.identity = {
            userId: stored.userId,
            deviceId: stored.deviceId,
            signaturePublicKey: fromBase64(stored.publicKey),
            signaturePrivateKey: fromBase64(stored.privateKey),
        };
        this.deviceKeys.set(`${this.userId}/${stored.deviceId}`, this.identity.signaturePublicKey);
        await this.refillKeyPackages();
        await this.processWelcomes();
    }
    async wipe() {
        for (const k of await this.store.keys(''))
            await this.store.delete(k);
        this.groups.clear();
        this.identity = null;
    }
    async refillKeyPackages(force = false) {
        const id = this.requireIdentity();
        const count = await this.api.keyPackageCount(id.deviceId);
        if (!force && count >= KEY_PACKAGE_REFILL_BELOW)
            return;
        const { publish, store } = await generateKeyPackages(id, KEY_PACKAGE_TARGET - Math.min(count, KEY_PACKAGE_TARGET - 1));
        for (const kp of store)
            await this.store.set(`kp:${kp.ref}`, kp);
        await this.api.uploadKeyPackages(id.deviceId, publish);
    }
    requireIdentity() {
        if (!this.identity)
            throw new Error('E2EE engine not initialized');
        return this.identity;
    }
    /* ------------------------------------------------------------ key lookup */
    async resolveKey(m) {
        const key = `${m.userId}/${m.deviceId}`;
        const cached = this.deviceKeys.get(key);
        if (cached)
            return cached;
        const devices = await this.api.userDevices(m.userId);
        for (const d of devices)
            this.deviceKeys.set(`${d.userId}/${d.id}`, fromBase64(d.identityKey));
        return this.deviceKeys.get(key) ?? null;
    }
    /** Called when the server reports a device list; detects key changes for a user. */
    noteDevices(devices) {
        for (const d of devices) {
            const key = `${d.userId}/${d.id}`;
            const known = this.deviceKeys.get(key);
            const fresh = fromBase64(d.identityKey);
            if (known && !bytesEqual(known, fresh)) {
                throw new Error(`identity key changed for device ${d.id} — possible tampering`);
            }
            this.deviceKeys.set(key, fresh);
        }
    }
    /* ----------------------------------------------------------- serialization */
    /** Per-channel mutex: MLS operations on a group must be strictly sequential. */
    withLock(channelId, fn) {
        const prev = this.locks.get(channelId) ?? Promise.resolve();
        const next = prev.catch(() => { }).then(fn);
        this.locks.set(channelId, next.catch(() => { }));
        return next;
    }
    async loadGroup(channelId, generation) {
        const cached = this.groups.get(`${channelId}:${generation}`);
        if (cached)
            return cached;
        const bytes = await this.store.get(`group:${channelId}:${generation}`);
        if (!bytes)
            return null;
        const g = deserializeGroup(bytes, this.config);
        this.groups.set(`${channelId}:${generation}`, g);
        return g;
    }
    async saveGroup(channelId, generation, g) {
        const prev = this.groups.get(`${channelId}:${generation}`);
        this.groups.set(`${channelId}:${generation}`, g);
        if (!prev || groupEpoch(prev) !== groupEpoch(g)) {
            this.emit({ type: 'epoch', channelId, generation, epoch: groupEpoch(g) });
        }
        await this.store.set(`group:${channelId}:${generation}`, serializeGroup(g));
    }
    async meta(channelId) {
        return ((await this.store.get(`meta:${channelId}`)) ?? {
            generation: -1,
            cursor: null,
            joinedAt: null,
        });
    }
    setMeta(channelId, m) {
        return this.store.set(`meta:${channelId}`, m);
    }
    /* --------------------------------------------------------------- welcomes */
    /** Join any groups we've been added to. */
    async processWelcomes() {
        const id = this.requireIdentity();
        const welcomes = await this.api.welcomes(id.deviceId);
        for (const w of welcomes)
            await this.withLock(w.channelId, () => this.applyWelcome(w));
    }
    async onWelcome(w) {
        await this.withLock(w.channelId, () => this.applyWelcome(w));
    }
    async applyWelcome(w) {
        const id = this.requireIdentity();
        const meta = await this.meta(w.channelId);
        const existing = await this.loadGroup(w.channelId, w.generation);
        if (!existing || meta.generation < w.generation) {
            const joined = await joinFromWelcome(w.welcome, (ref) => this.store.get(`kp:${ref}`), this.config).catch(() => null);
            if (joined) {
                await this.store.delete(`kp:${joined.usedRef}`);
                await this.saveGroup(w.channelId, w.generation, joined.group);
                await this.setMeta(w.channelId, {
                    generation: w.generation,
                    cursor: w.commitId,
                    joinedAt: w.commitId,
                });
                this.emit({ type: 'status', channelId: w.channelId, status: 'ready' });
            }
        }
        await this.api.deleteWelcome(id.deviceId, w.id);
        await this.syncLocked(w.channelId);
    }
    /* ------------------------------------------------------------------ sync */
    /** Bring a channel up to date. Safe to call often (e.g. on every gateway event). */
    sync(channelId) {
        return this.withLock(channelId, () => this.syncLocked(channelId).catch((err) => {
            // Don't leave the channel looking busy ('syncing') or unknown after a failure.
            const status = this.status.get(channelId);
            if (status === undefined || status === 'syncing')
                this.emit({ type: 'status', channelId, status: 'error' });
            throw err;
        }));
    }
    async syncLocked(channelId, attempt = 0) {
        const state = await this.api.state(channelId);
        let meta = await this.meta(channelId);
        if (meta.generation !== state.generation) {
            // New group generation (first contact or reset): forget the old cursor.
            meta = { generation: state.generation, cursor: null, joinedAt: null };
            await this.setMeta(channelId, meta);
        }
        let group = await this.loadGroup(channelId, state.generation);
        if (!group) {
            if (state.epoch === 0) {
                group = await this.createGroup(channelId, state);
                // Lost the race: take the winner's welcome, or join their group on our own.
                if (!group)
                    return attempt < 3 ? this.syncLocked(channelId, attempt + 1) : undefined;
            }
            else {
                // Someone may have added us already; otherwise join on our own.
                await this.processWelcomesFor(channelId);
                if (await this.loadGroup(channelId, state.generation))
                    return; // synced by the welcome
                const joined = await this.joinExternally(channelId, state);
                if (joined === 'conflict' && attempt < 3)
                    return this.syncLocked(channelId, attempt + 1);
                if (!joined || joined === 'conflict') {
                    this.emit({ type: 'status', channelId, status: 'waiting-for-keys' });
                    this.requestKeys(channelId);
                    return;
                }
                group = joined;
                meta = await this.meta(channelId);
            }
        }
        // Process the server's ordered log after our cursor.
        try {
            for (;;) {
                const batch = await this.api.messages(channelId, meta.cursor ?? undefined);
                for (const m of batch.filter((x) => x.generation === state.generation)) {
                    group = await this.processOne(channelId, state.generation, group, m);
                }
                if (batch.length > 0) {
                    meta = { ...meta, cursor: batch.at(-1).id };
                    await this.setMeta(channelId, meta);
                }
                if (batch.length < 100)
                    break;
            }
        }
        catch (err) {
            if (!(err instanceof BrokenGroupError))
                throw err;
            if (attempt >= 3) {
                this.emit({ type: 'status', channelId, status: 'error' });
                throw err;
            }
            // Drop the unusable state and rejoin the group on our own (replacing our old leaf).
            this.groups.delete(`${channelId}:${state.generation}`);
            await this.store.delete(`group:${channelId}:${state.generation}`);
            return this.syncLocked(channelId, attempt + 1);
        }
        const committed = await this.reconcile(channelId, state, group);
        if (!committed && groupEpoch(group) === state.epoch && state.groupInfoEpoch !== state.epoch) {
            // A group from before GroupInfo was published: publish it so new devices can join.
            await this.api
                .putGroupInfo(channelId, {
                deviceId: this.requireIdentity().deviceId,
                generation: state.generation,
                epoch: state.epoch,
                groupInfo: await exportGroupInfo(group),
            })
                .catch((err) => {
                if (!(err instanceof ConflictError))
                    throw err;
            });
        }
        this.emit({ type: 'status', channelId, status: 'ready' });
    }
    /** Ask online participants to let this device in (at most every 30 s per conversation). */
    requestKeys(channelId) {
        const last = this.keysRequestedAt.get(channelId) ?? 0;
        if (Date.now() - last < 30_000)
            return;
        this.keysRequestedAt.set(channelId, Date.now());
        void this.api.requestKeys(channelId, this.requireIdentity().deviceId).catch(() => { });
    }
    /** Another device asked for keys: try adding it again now, even if it lacked key packages. */
    onKeysRequested(channelId, deviceId) {
        if (deviceId === this.deviceId)
            return Promise.resolve();
        this.noKeyPackagesUntil.delete(deviceId);
        return this.sync(channelId);
    }
    /**
     * Join the group from its published GroupInfo (an MLS external commit), so a new device
     * can send right away instead of waiting for another member to come online and add it.
     * Returns null if there is no usable GroupInfo, 'conflict' if the group moved on first.
     */
    async joinExternally(channelId, state) {
        const id = this.requireIdentity();
        if (state.groupInfoEpoch !== state.epoch)
            return null;
        const info = await this.api.groupInfo(channelId);
        if (!info || info.generation !== state.generation || info.epoch !== state.epoch)
            return null;
        this.emit({ type: 'status', channelId, status: 'syncing' });
        this.noteDevices(state.devices);
        const joined = await joinExternal(id, info.groupInfo, this.config);
        let posted;
        try {
            posted = await this.api.post(channelId, {
                deviceId: id.deviceId,
                kind: 'commit',
                generation: state.generation,
                epoch: info.epoch,
                ciphertext: joined.commit,
                memberDeviceIds: groupMembers(joined.group).map((m) => m.deviceId),
                groupInfo: await exportGroupInfo(joined.group),
            });
        }
        catch (err) {
            if (err instanceof ConflictError)
                return 'conflict';
            throw err;
        }
        await this.saveGroup(channelId, state.generation, joined.group);
        // Messages before our join were encrypted for others; start reading after it.
        await this.setMeta(channelId, {
            generation: state.generation,
            cursor: posted.id,
            joinedAt: posted.id,
        });
        return joined.group;
    }
    async processOne(channelId, generation, group, m) {
        const id = this.requireIdentity();
        if (m.senderDeviceId === id.deviceId)
            return group; // our own messages were applied when sent
        try {
            const res = await processIncoming(group, m.ciphertext);
            await this.saveGroup(channelId, generation, res.group);
            if (res.kind === 'application') {
                if (!res.sender ||
                    res.sender.userId !== m.senderUserId ||
                    res.sender.deviceId !== m.senderDeviceId) {
                    throw new Error('sender mismatch between server metadata and MLS credential');
                }
                await this.appendLog(channelId, {
                    serverId: m.id,
                    channelId,
                    senderUserId: m.senderUserId,
                    senderDeviceId: m.senderDeviceId,
                    createdAt: m.createdAt,
                    envelope: openEnvelope(res.plaintext),
                });
            }
            else if (res.removedSelf) {
                this.emit({ type: 'status', channelId, status: 'waiting-for-keys' });
            }
            return res.group;
        }
        catch (err) {
            if (m.kind === 'application') {
                await this.appendLog(channelId, {
                    serverId: m.id,
                    channelId,
                    senderUserId: m.senderUserId,
                    senderDeviceId: m.senderDeviceId,
                    createdAt: m.createdAt,
                    envelope: null,
                    error: err instanceof Error ? err.message : 'decryption failed',
                });
                return group;
            }
            // A commit we can't apply leaves our state unusable (sync rejoins the group).
            throw new BrokenGroupError(err instanceof Error ? err.message : 'bad commit', {
                cause: err,
            });
        }
    }
    /** Create the group and add every participant's devices. Returns null if another member won. */
    async createGroup(channelId, state) {
        const id = this.requireIdentity();
        this.emit({ type: 'status', channelId, status: 'syncing' });
        this.noteDevices(state.devices);
        const fresh = await createDmGroup(id, `${channelId}:${state.generation}`, this.config);
        const targets = state.devices.filter((d) => d.id !== id.deviceId && !d.revoked);
        const res = await this.commitTo(channelId, state.generation, fresh, targets, [], true);
        if (!res) {
            // Someone else created it first; their welcome will include us.
            await this.processWelcomesFor(channelId);
            return null;
        }
        return res;
    }
    async processWelcomesFor(channelId) {
        const id = this.requireIdentity();
        const ws = (await this.api.welcomes(id.deviceId)).filter((w) => w.channelId === channelId);
        for (const w of ws)
            await this.applyWelcome(w);
    }
    /**
     * Make the group's device roster match the participants' active devices.
     * Any member may do this; the server's epoch check serializes competing attempts.
     */
    async reconcile(channelId, state, group) {
        const id = this.requireIdentity();
        this.noteDevices(state.devices);
        const members = groupMembers(group);
        const inGroup = new Set(members.map((m) => m.deviceId));
        const wanted = state.devices.filter((d) => !d.revoked && state.recipientIds.includes(d.userId));
        const wantedIds = new Set(wanted.map((d) => d.id));
        // A device without key packages left (say, an install that's been offline for a while)
        // can't be added; it joins on its own (external commit) when it next opens the DM.
        const toAdd = wanted.filter((d) => !inGroup.has(d.id) &&
            d.id !== id.deviceId &&
            (this.noKeyPackagesUntil.get(d.id) ?? 0) <= Date.now());
        const toRemove = members.filter((m) => !wantedIds.has(m.deviceId) && m.deviceId !== id.deviceId);
        if (toAdd.length === 0 && toRemove.length === 0)
            return false;
        if (groupEpoch(group) !== state.epoch)
            return false; // not caught up; next sync will retry
        return (await this.commitTo(channelId, state.generation, group, toAdd, toRemove)) !== null;
    }
    async commitTo(channelId, generation, group, add, remove, creating = false) {
        const id = this.requireIdentity();
        let claimed = [];
        if (add.length) {
            const userIds = [...new Set(add.map((d) => d.userId))];
            claimed = (await this.api.claimKeyPackages(userIds, add.map((d) => d.id))).keyPackages;
            const got = new Set(claimed.map((c) => c.deviceId));
            for (const d of add)
                if (!got.has(d.id))
                    this.noKeyPackagesUntil.set(d.id, Date.now() + NO_KEY_PACKAGES_RETRY_MS);
        }
        // Nothing to change: don't commit. An empty commit would still start a new epoch (and
        // a new call key), and every member seeing the same unaddable device would keep doing it.
        if (!creating && claimed.length === 0 && remove.length === 0)
            return null;
        const out = await commitMembershipChange(group, { addKeyPackages: claimed.map((c) => c.keyPackage), removeDevices: remove }, (m) => this.resolveKey(m));
        const roster = groupMembers(out.group).map((m) => m.deviceId);
        try {
            await this.api.post(channelId, {
                deviceId: id.deviceId,
                kind: 'commit',
                generation,
                epoch: groupEpoch(group),
                ciphertext: out.commit,
                ...(out.welcome && claimed.length
                    ? { welcome: { welcome: out.welcome, deviceIds: claimed.map((c) => c.deviceId) } }
                    : {}),
                memberDeviceIds: roster,
                groupInfo: await exportGroupInfo(out.group),
            });
        }
        catch (err) {
            // Not accepted, so the current state stays in use. Reload it from storage in case
            // building the commit touched the in-memory copy.
            this.groups.delete(`${channelId}:${generation}`);
            if (err instanceof ConflictError)
                return null;
            throw err;
        }
        out.zeroConsumed();
        await this.saveGroup(channelId, generation, out.group);
        const meta = await this.meta(channelId);
        await this.setMeta(channelId, { ...meta, generation });
        return out.group;
    }
    /** Abandon the current group and start a new generation (recovery). */
    async resetGroup(channelId) {
        await this.withLock(channelId, async () => {
            const state = await this.api.state(channelId);
            try {
                await this.api.reset(channelId, state.generation);
            }
            catch (err) {
                if (!(err instanceof ConflictError))
                    throw err;
            }
            await this.syncLocked(channelId);
        });
    }
    /* ------------------------------------------------------------- call keys */
    /**
     * Key for end-to-end encrypted calls in this DM, derived from the current MLS epoch.
     * Returns null if this device isn't (yet) a member of the conversation's group.
     */
    async callKey(channelId) {
        return this.withLock(channelId, async () => {
            await this.syncLocked(channelId).catch(() => {
                if (this.status.get(channelId) === 'syncing')
                    this.emit({ type: 'status', channelId, status: 'error' });
            });
            const meta = await this.meta(channelId);
            const group = await this.loadGroup(channelId, meta.generation);
            if (!group)
                return null;
            const context = new TextEncoder().encode(`${channelId}:${meta.generation}`);
            return {
                key: await exportGroupSecret(group, 'comms call v1', context, 32),
                generation: meta.generation,
                epoch: groupEpoch(group),
            };
        });
    }
    /* --------------------------------------------------------------- sending */
    async send(channelId, envelope) {
        return this.withLock(channelId, async () => {
            const id = this.requireIdentity();
            let posted = null;
            // The server refuses a message for an epoch that's no longer current (someone joined
            // or left meanwhile, and they couldn't read it): catch up and encrypt it again.
            for (let attempt = 0; !posted; attempt++) {
                await this.syncLocked(channelId);
                const meta = await this.meta(channelId);
                const group = await this.loadGroup(channelId, meta.generation);
                if (!group)
                    throw new Error('Encryption keys for this conversation are not available yet');
                const { group: next, ciphertext, epoch, } = await encryptApplicationMessage(group, sealEnvelope(envelope));
                await this.saveGroup(channelId, meta.generation, next);
                try {
                    posted = await this.api.post(channelId, {
                        deviceId: id.deviceId,
                        kind: 'application',
                        generation: meta.generation,
                        epoch,
                        ciphertext,
                    });
                }
                catch (err) {
                    if (!(err instanceof ConflictError) || attempt >= 2)
                        throw err;
                }
            }
            const entry = {
                serverId: posted.id,
                channelId,
                senderUserId: this.userId,
                senderDeviceId: id.deviceId,
                createdAt: posted.createdAt,
                envelope,
            };
            await this.appendLog(channelId, entry);
            return entry;
        });
    }
    /* ------------------------------------------------------------ local log */
    async appendLog(channelId, entry) {
        const log = (await this.store.get(`log:${channelId}`)) ?? [];
        if (log.some((e) => e.serverId === entry.serverId))
            return;
        log.push(entry);
        await this.store.set(`log:${channelId}`, log);
        if (entry.envelope?.type === 'typing') {
            this.emit({ type: 'typing', channelId, userId: entry.senderUserId });
        }
        else {
            this.emit({ type: 'messages', channelId });
        }
    }
    async forgetServerMessage(channelId, serverId) {
        const log = (await this.store.get(`log:${channelId}`)) ?? [];
        await this.store.set(`log:${channelId}`, log.filter((e) => e.serverId !== serverId));
        this.emit({ type: 'messages', channelId });
    }
    /** Materialize the conversation: apply edits, deletes and reactions (only the author may edit/delete). */
    async messages(channelId) {
        const log = (await this.store.get(`log:${channelId}`)) ?? [];
        const byId = new Map();
        const order = [];
        for (const e of log) {
            const env = e.envelope;
            if (!env) {
                byId.set(e.serverId, {
                    id: e.serverId,
                    serverId: e.serverId,
                    channelId,
                    authorId: e.senderUserId,
                    content: '',
                    attachments: [],
                    replyTo: null,
                    createdAt: e.createdAt,
                    editedAt: null,
                    reactions: [],
                    undecryptable: true,
                });
                order.push(e.serverId);
                continue;
            }
            if (env.type === 'message') {
                byId.set(env.id, {
                    id: env.id,
                    serverId: e.serverId,
                    channelId,
                    authorId: e.senderUserId,
                    content: env.content,
                    attachments: env.attachments ?? [],
                    replyTo: env.replyTo ?? null,
                    createdAt: e.createdAt,
                    editedAt: null,
                    reactions: [],
                    undecryptable: false,
                });
                order.push(env.id);
            }
            else if (env.type === 'edit') {
                const m = byId.get(env.target);
                if (m && m.authorId === e.senderUserId)
                    Object.assign(m, { content: env.content, editedAt: e.createdAt });
            }
            else if (env.type === 'delete') {
                const m = byId.get(env.target);
                if (m && m.authorId === e.senderUserId)
                    byId.delete(env.target);
            }
            else if (env.type === 'reaction') {
                const m = byId.get(env.target);
                if (!m)
                    continue;
                let r = m.reactions.find((x) => x.emoji === env.emoji);
                if (!r && !env.remove)
                    m.reactions.push((r = { emoji: env.emoji, userIds: [] }));
                if (!r)
                    continue;
                r.userIds = env.remove
                    ? r.userIds.filter((u) => u !== e.senderUserId)
                    : [...new Set([...r.userIds, e.senderUserId])];
                m.reactions = m.reactions.filter((x) => x.userIds.length > 0);
            }
        }
        return order.map((id) => byId.get(id)).filter((m) => !!m);
    }
}
//# sourceMappingURL=engine.js.map